import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import type { VaultItem } from '../../library/vault/vaultData'
import { EditPostSheet } from '../EditPostSheet'
import { OfHtml } from '../OfHtml'
import type { EditPostPatch } from '../editPost'
import type { OfApi } from '../ofApi'
import { MediaThumb } from '../PostCard'
import type { ScheduleItem } from './queueData'
import { formatDate, formatTime, TYPE_ICON } from './queueFormat'

/** What the page does for an item's ··· menu (OnlyFans writes; SPEC §4). */
export interface QueueActions {
  canWrite: boolean
  accountId: string
  of: OfApi
  timeZone: string
  isPinned: (item: ScheduleItem) => boolean
  pin: (item: ScheduleItem) => Promise<void>
  publish: (item: ScheduleItem) => Promise<void>
  remove: (item: ScheduleItem) => Promise<void>
  edit: (item: ScheduleItem, patch: EditPostPatch) => Promise<void>
  openMedia: (items: VaultItem[], index: number) => void
}

/** OnlyFans' expiry period ("a day"), counted from the publish time. */
function expiry(item: ScheduleItem, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  if (!item.expiresAt) return null
  const from = Date.parse(item.publishAt ?? item.post?.postedAt ?? '')
  const ms = Date.parse(item.expiresAt) - from
  if (!Number.isFinite(ms) || ms <= 0) return null
  const minutes = Math.round(ms / 60_000)
  if (minutes >= 1440 && minutes % 1440 === 0) return t('duration.days', { count: minutes / 1440 })
  if (minutes >= 60) return t('duration.hours', { count: Math.round(minutes / 60) })
  return t('duration.minutes', { count: minutes })
}

/** One queue item as the OnlyFans queue shows it: type, date and time, expiry, labels, text, media. */
export function QueueCard({ item, actions, mobile = false }: { item: ScheduleItem; actions: QueueActions; mobile?: boolean }) {
  const { t, i18n } = useTranslation('ofQueue')
  const at = item.publishAt ?? item.post?.postedAt ?? item.createdAt
  const ends = expiry(item, t)
  return (
    <article className="ofq-item" aria-label={`${t(`typeOne.${item.type}`)} ${at ? formatTime(at, actions.timeZone, i18n.language) : ''}`}>
      <header className="ofq-item-head">
        <span className={`ofq-type ${item.later ? 'is-later' : ''}`} title={t(`typeOne.${item.type}`)}>
          <FeatherIcon name={TYPE_ICON[item.type]} size={16} />
        </span>
        <div className="ofq-item-when">
          <span className="ofq-item-date">{at && formatDate(at, actions.timeZone, i18n.language)}</span>
          <span className="ofq-item-time" title={item.later ? t('laterTime') : undefined}>
            {at && formatTime(at, actions.timeZone, i18n.language)}
            {item.later && '*'}
          </span>
        </div>
        <span className="lib-spacer" />
        <div className="ofq-item-side">
          <div className="ofq-item-top">
            {item.later && <span className="ofq-later">{t('later')}</span>}
            <ItemMenu item={item} actions={actions} mobile={mobile} />
          </div>
          {ends && (
            <span className="ofq-item-exp" title={t('expires', { d: ends })}>
              {ends}
              <FeatherIcon name="clock" size={12} />
            </span>
          )}
        </div>
      </header>
      {actions.isPinned(item) && (
        <div className="ofq-item-pin">
          <FeatherIcon name="bookmark" size={12} /> {t('pinned')}
        </div>
      )}
      {item.post && item.post.labels.length > 0 && (
        <div className="ofp-post-labels ofq-item-labels">
          <FeatherIcon name="tag" size={12} />
          <span>{t('labels')}</span>
          {item.post.labels.map((l) => (
            <span key={l.id} className="ofp-dot-item">
              {l.name}
            </span>
          ))}
        </div>
      )}
      <OfHtml html={item.text} className="ofp-text ofq-item-text" />
      {item.media.length > 0 && <ItemMedia items={item.media} onOpen={(i) => actions.openMedia(item.media, i)} />}
    </article>
  )
}

function ItemMedia({ items, onOpen }: { items: VaultItem[]; onOpen: (i: number) => void }) {
  const { t } = useTranslation('ofQueue')
  const shown = items.length === 1 ? items : items.slice(0, 4)
  return (
    <div className={`ofp-media ofp-media--${Math.min(shown.length, 4)}`}>
      {shown.map((m, i) => (
        <MediaThumb key={m.id} item={m} big={items.length === 1} more={i === 3 && items.length > 4 ? items.length - 4 : 0} onOpen={() => onOpen(i)} label={t('openMedia')} />
      ))}
    </div>
  )
}

type Open = null | 'menu' | 'publish' | 'delete' | 'edit'

/**
 * The item's ··· menu: Pin / Edit / Publish now / Delete, as on OnlyFans.
 * Only posts get it: the message and stream entity shapes were never captured.
 */
export function ItemMenu({ item, actions, mobile = false }: { item: ScheduleItem; actions: QueueActions; mobile?: boolean }) {
  const { t } = useTranslation('ofQueue')
  const [open, setOpen] = useState<Open>(null)
  const [busy, setBusy] = useState(false)
  const post = item.post
  if (!actions.canWrite || !post) return null

  const run = (work: () => Promise<void>) => async () => {
    if (busy) return
    setOpen(null)
    setBusy(true)
    try {
      await work()
    } catch {
      // The page already said why (toast).
    } finally {
      setBusy(false)
    }
  }
  const pinned = actions.isPinned(item)
  const confirm = (kind: 'publish' | 'delete') => (
    <ConfirmPopover
      title={t(`${kind}Title`)}
      body={t(`${kind}Body`)}
      confirmLabel={t(`${kind}Confirm`)}
      onConfirm={run(() => (kind === 'publish' ? actions.publish(item) : actions.remove(item)))}
      onCancel={() => setOpen(null)}
    />
  )

  return (
    <div className="lib-anchor">
      <button
        type="button"
        className={`lib-icon-btn ${mobile ? 'ofq-more--m' : ''}`}
        aria-label={t('actions')}
        aria-expanded={open === 'menu'}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(open === 'menu' ? null : 'menu')
        }}
      >
        <FeatherIcon name="more-horizontal" size={18} />
      </button>
      {open === 'menu' && (
        <>
          <div className="lib-backdrop" onClick={() => setOpen(null)} />
          <div className="lib-pop lib-menu lib-menu--right" role="menu" aria-label={t('actions')} onClick={(e) => e.stopPropagation()}>
            <MenuItem label={pinned ? t('unpin') : t('pin')} onPress={run(() => actions.pin(item))} />
            {post.canEdit && <MenuItem label={t('edit')} onPress={() => setOpen('edit')} />}
            <MenuItem label={t('publish')} onPress={() => setOpen('publish')} />
            {post.canDelete && <MenuItem label={t('delete')} danger onPress={() => setOpen('delete')} />}
          </div>
        </>
      )}
      {open === 'publish' && confirm('publish')}
      {open === 'delete' && confirm('delete')}
      {open === 'edit' && (
        <EditPostSheet
          post={post}
          accountId={actions.accountId}
          of={actions.of}
          busy={busy}
          expireFrom={item.publishAt ?? post.postedAt}
          onClose={() => setOpen(null)}
          onSave={(patch) => {
            setBusy(true)
            actions
              .edit(item, patch)
              .then(() => setOpen(null))
              .catch(() => undefined)
              .finally(() => setBusy(false))
          }}
        />
      )}
    </div>
  )
}

function MenuItem({ label, onPress, danger = false }: { label: string; onPress: () => void; danger?: boolean }) {
  return (
    <button type="button" role="menuitem" className={`lib-menu-item ${danger ? 'lib-menu-item--danger' : ''}`} onClick={onPress}>
      <span className="lib-menu-label">{label}</span>
    </button>
  )
}
